import React, { useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { USERSROLE } from 'types';
import { SideNav, SideNavItems } from 'carbon-components-react';
import UserContext from 'contexts/UserConext';
import {
  END_USER_PREMIUM_USERROLES,
  VIEW_SUPPORT_TOOLS_LEFT_NAV,
  SCREEN_SIZE
} from 'types';
import Utils from 'utilities/helper.util';
import {
  LeftNavSysAdmin,
  LeftNavDBYDAdmin,
  LeftNavAOAdminAdmin,
  LeftNavAOViewer,
  LeftNavDeveloperTools
} from '..';
import LeftNavCommonMenuItem from './leftNav.CommonMenuItem';
import LeftNavPremiumFeatures from './leftNav.PremiumFeatures';
import { useSelector } from 'react-redux';
import { currentUser, selectScreenSize } from 'redux/session/selectors';
import { useEventListener } from 'utilities/hooks.util';
import { hasResidentialUserType } from 'utilities/residential-redirect.util';

const bigScreens = [SCREEN_SIZE.MAX, SCREEN_SIZE.XL, SCREEN_SIZE.LG];

export const LeftHandSideNav: React.FC<Props> = (props: Props) => {
  const { isSideNavExpanded, setIsSideNavExpanded } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const user = useSelector(currentUser);
  const screenSize = useSelector(selectScreenSize);
  const isDeveloperActive = user?.preferences?.isDeveloperActive || false;
  const isAADBYD = !user?.Organisation?.tenantId;
  const hidePremiumFeatures = hasResidentialUserType(user);

  const handleClickOutside = useCallback(
    (evt: any) => {
      if (
        containerRef.current &&
        evt.target &&
        !bigScreens.includes(screenSize)
      ) {
        const burgerMenuButton = document
          .getElementsByClassName('burgerMenuButton')
          ?.item(0);
        if (
          !containerRef.current.contains(evt.target) &&
          !burgerMenuButton?.contains(evt.target)
        ) {
          typeof setIsSideNavExpanded === 'function' &&
            setIsSideNavExpanded(false);
        }
      }
    },
    [screenSize, setIsSideNavExpanded]
  );

  useEventListener('mousedown', handleClickOutside);
  useEventListener('touchstart', handleClickOutside);

  return (
    <UserContext.Consumer>
      {(userRole: any) => {
        return (
          <>
            <SideNav
              ref={containerRef}
              isFixedNav
              expanded={
                isSideNavExpanded ||
                (screenSize && bigScreens.includes(screenSize) ? true : false)
              }
              isChildOfHeader={true}
              aria-label="Side navigation"
              isRail
              defaultExpanded={
                screenSize && screenSize === SCREEN_SIZE.MAX ? true : false
              }
              className={`leftHandSideNav ${
                isSideNavExpanded ? 'expanded' : ''
              }`}
              data-testid="leftHandSideNav"
            >
              <SideNavItems isSideNavExpanded={true}>
                <LeftNavCommonMenuItem />
                {Utils.isValidUserRole(END_USER_PREMIUM_USERROLES, userRole) &&
                process.env.REACT_APP_PREMIUM_SELF_SERVICE_ENABLED === 'true' &&
                !hidePremiumFeatures ? (
                  <LeftNavPremiumFeatures />
                ) : null}
                {screenSize && bigScreens.includes(screenSize) && (
                  <>
                    {USERSROLE.DBYDADMIN === userRole ? (
                      <LeftNavDBYDAdmin
                        screenSize={screenSize}
                        isAADBYD={isAADBYD}
                      />
                    ) : null}
                    {USERSROLE.AOADMIN === userRole ? (
                      <LeftNavAOAdminAdmin screenSize={screenSize} />
                    ) : null}
                    {USERSROLE.AOVIEWER === userRole ? (
                      <LeftNavAOViewer screenSize={screenSize} />
                    ) : null}
                    {Utils.isValidUserRole(
                      VIEW_SUPPORT_TOOLS_LEFT_NAV,
                      userRole
                    ) ? (
                      <LeftNavSysAdmin />
                    ) : null}
                    {USERSROLE.ENQUIRER === userRole && isDeveloperActive && (
                      <div className="divider" />
                    )}
                    {isDeveloperActive && <LeftNavDeveloperTools />}
                  </>
                )}
              </SideNavItems>
            </SideNav>
          </>
        );
      }}
    </UserContext.Consumer>
  );
};

const propTypes = {
  isSideNavExpanded: PropTypes.bool.isRequired,
  setIsSideNavExpanded: PropTypes.func
};

type Props = PropTypes.InferProps<typeof propTypes>;

LeftHandSideNav.propTypes = propTypes;

export default LeftHandSideNav;
